// Deutsche Labels & Optionen fürs Medizin-Modul (Frontend).

export const CATEGORY_LABELS: Record<string, string> = {
  disease: 'Krankheit',
  hereditary: 'Erbkrankheit',
  cause_of_death: 'Todesursache',
};

export const INHERITANCE_LABELS: Record<string, string> = {
  autosomal_dominant: 'autosomal-dominant',
  autosomal_recessive: 'autosomal-rezessiv',
  x_linked_dominant: 'X-chromosomal-dominant',
  x_linked_recessive: 'X-chromosomal-rezessiv',
  y_linked: 'Y-chromosomal',
  mitochondrial: 'mitochondrial',
  multifactorial: 'multifaktoriell (polygen)',
  imprinted: 'genomisch geprägt',
  unknown: 'unbekannt',
};

export const STATUS_LABELS: Record<string, string> = {
  active: 'aktiv',
  resolved: 'ausgeheilt',
  chronic: 'chronisch',
  suspected: 'Verdacht',
  unknown: 'unbekannt',
};
export const STATUS_OPTIONS = ['active', 'chronic', 'resolved', 'suspected', 'unknown'] as const;

export const ASCERTAINMENT_LABELS: Record<string, string> = {
  not_assessed: 'nicht untersucht',
  assessed_negative: 'untersucht – kein Befund',
  assessed_positive: 'untersucht – bestätigt',
};
export const ASCERTAINMENT_OPTIONS = ['not_assessed', 'assessed_negative', 'assessed_positive'] as const;

export const CONDITION_CERTAINTY_LABELS: Record<string, string> = {
  documented: 'belegt',
  reported: 'berichtet',
  suspected: 'vermutet',
};
export const CONDITION_CERTAINTY_OPTIONS = ['documented', 'reported', 'suspected'] as const;

export const TRAIT_CERTAINTY_LABELS: Record<string, string> = {
  confirmed: 'bestätigt',
  reported: 'berichtet',
  inferred: 'abgeleitet',
  family_lore: 'Familienüberlieferung',
};
export const TRAIT_CERTAINTY_OPTIONS = ['confirmed', 'reported', 'inferred', 'family_lore'] as const;

// Pedigree-Status-Symbol aus Erfassungsgrad ableiten (medizinischer Standard).
export function statusSymbol(ascertainment: string): { symbol: string; label: string; color: string } {
  switch (ascertainment) {
    case 'assessed_positive':
      return { symbol: '●', label: 'betroffen', color: 'text-slate-900' };
    case 'assessed_negative':
      return { symbol: '○', label: 'nicht betroffen', color: 'text-slate-400' };
    default:
      return { symbol: '?', label: 'unbekannt', color: 'text-amber-500' };
  }
}

// ─── Merkmals-Typen & vordefinierte Wertelisten ─────────────────────────────

export interface TraitTypeDef {
  type: string;
  label: string;
  kind: 'code' | 'number' | 'text';
  unit?: string;
  options?: Array<{ value: string; label: string }>;
  /** Bietet eine „Sonstiges"-Option mit Freitextfeld (value_code='other' + value_text). */
  allowFreeText?: boolean;
}

/** Code-Wert für die „Sonstiges"-Option kategorischer Merkmale. */
export const TRAIT_OTHER_CODE = 'other';

export const TRAIT_TYPES: TraitTypeDef[] = [
  {
    type: 'eye_color',
    label: 'Augenfarbe',
    kind: 'code',
    allowFreeText: true,
    options: [
      { value: 'blue', label: 'Blau' },
      { value: 'green', label: 'Grün' },
      { value: 'hazel', label: 'Haselnuss' },
      { value: 'brown', label: 'Braun' },
      { value: 'gray', label: 'Grau' },
      { value: 'amber', label: 'Bernstein' },
    ],
  },
  {
    type: 'hair_color',
    label: 'Haarfarbe',
    kind: 'code',
    allowFreeText: true,
    options: [
      { value: 'blond', label: 'Blond' },
      { value: 'brown', label: 'Braun' },
      { value: 'black', label: 'Schwarz' },
      { value: 'red', label: 'Rot' },
      { value: 'gray', label: 'Grau/Weiß' },
    ],
  },
  {
    type: 'blood_group',
    label: 'Blutgruppe (AB0)',
    kind: 'code',
    options: [
      { value: 'A', label: 'A' },
      { value: 'B', label: 'B' },
      { value: 'AB', label: 'AB' },
      { value: '0', label: '0' },
    ],
  },
  {
    type: 'rh_factor',
    label: 'Rhesusfaktor',
    kind: 'code',
    options: [
      { value: 'positive', label: 'Rh+' },
      { value: 'negative', label: 'Rh−' },
    ],
  },
  { type: 'height_cm', label: 'Körpergröße', kind: 'number', unit: 'cm' },
  {
    type: 'handedness',
    label: 'Händigkeit',
    kind: 'code',
    allowFreeText: true,
    options: [
      { value: 'right', label: 'Rechts' },
      { value: 'left', label: 'Links' },
      { value: 'ambidextrous', label: 'Beidhändig' },
    ],
  },
];

export const TRAIT_TYPE_BY_KEY: Record<string, TraitTypeDef> = Object.fromEntries(
  TRAIT_TYPES.map((t) => [t.type, t]),
);

export function formatTraitValue(t: { traitType: string; valueCode: string | null; valueNumber: number | null; valueText: string | null }): string {
  const def = TRAIT_TYPE_BY_KEY[t.traitType];
  // „Sonstiges": den erfassten Freitext anzeigen (nicht das Code-Wort).
  if (t.valueCode === TRAIT_OTHER_CODE) return t.valueText?.trim() || 'Sonstiges';
  if (t.valueCode && def?.options) {
    return def.options.find((o) => o.value === t.valueCode)?.label ?? t.valueCode;
  }
  if (t.valueNumber != null) return `${t.valueNumber}${def?.unit ? ' ' + def.unit : ''}`;
  return t.valueText ?? t.valueCode ?? '—';
}
